import React, { Suspense } from 'react';
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';

import MobileCommonLayout from 'components/templates/layouts/mobile/MobileCommonLayout';
import MobilePastLayout from 'components/templates/layouts/mobile/MobilePastLayout';
// Layout imports
import CommonLayout from 'components/templates/layouts/web/CommonLayout';
import MainLayout from 'components/templates/layouts/web/MainLayout';
import PastLayout from 'components/templates/layouts/web/PastLayout';

import useViewMode from 'hooks/common/useViewMode';

// Routes data
import ProtectedRoute from './ProtectedRoute';
import RouteMetaWrapper from './RouteMetaWrapper';
import { COMPONENTS, LEGACY_REDIRECTS, ROUTE_DATA } from './routes';

const PagesRouter = () => {
  const { isMobile } = useViewMode();

  const convertElement = (item) => {
    if (!item.component) return <Navigate to="/" replace />;

    // 이미 JSX라면 그대로 반환
    if (typeof item.component !== 'string') return item.component;

    const Component = COMPONENTS[item.component];
    if (!Component) return <Navigate to="/" replace />;

    // 전달할 props를 동적으로 구성
    const props = {};
    if (item.type) props.type = item.type;
    if (item.title) props.title = item.title;
    if (item.description) props.description = item.description;
    if (item.features) props.features = item.features;
    if (item.useCases) props.useCases = item.useCases;
    if (item.links) props.links = item.links;
    if (item.exampleDesc) props.exampleDesc = item.exampleDesc;
    if (item.apiPath) props.apiPath = item.apiPath;

    let elementJSX = <Component {...props} />;

    // 메타 태그 추가
    if (item.meta || item.title || item.description)
      elementJSX = (
        <RouteMetaWrapper
          meta={item.meta ? { ...item.meta, path: item.path } : null}
          title={item.title}
          description={item.description}
          features={item.features}
          useCases={item.useCases}
          links={item.links}
          exampleDesc={item.exampleDesc}
          aeo={item.meta?.aeo}
        >
          {elementJSX}
        </RouteMetaWrapper>
      );

    // --- 메타데이터 적용 (순서 중요) ---

    // 1. 보호 라우트 (인증 체크 초기 진입점)
    if (item.protected) {
      elementJSX = (
        <ProtectedRoute
          element={elementJSX}
          title={item.title}
          description={item.description}
          features={item.features}
          useCases={item.useCases}
        />
      );
    }

    // // 2. 에러 바운더리
    // if (item.error) {
    //   elementJSX = (
    //     <ErrorBoundary title={item.title} description={item.description} features={item.features} useCases={item.useCases}>
    //       {elementJSX}
    //     </ErrorBoundary>
    //   );
    // }

    return elementJSX;
  };

  return (
    <Suspense fallback={<></>}>
      <RouterProvider
        router={createBrowserRouter(
          [
            {
              element: isMobile ? <MobileCommonLayout /> : <CommonLayout />,
              children: [
                {
                  element: <MainLayout />,
                  children: [
                    {
                      path: '/',
                      element: convertElement(ROUTE_DATA['main']),
                    },
                  ],
                },
                {
                  element: isMobile ? <MobilePastLayout /> : <PastLayout />,
                  children: Object.values(ROUTE_DATA)
                    .flatMap((group) =>
                      Array.isArray(group) ? group : [group]
                    )
                    .filter((p) => p.path && p.path !== '/')
                    .map((p) => ({
                      path: p.path,
                      element: convertElement(p),
                    })),
                },
                // 경로 변경 이전 주소 리다이렉트 (검색엔진·llms.txt에 남은 링크 대응)
                ...Object.entries(LEGACY_REDIRECTS).map(([from, to]) => ({
                  path: from,
                  element: <Navigate to={to} replace />,
                })),
                isMobile
                  ? {
                      path: '/*',
                      element: <Navigate to="/?view=mobile" replace />,
                    }
                  : {
                      path: '/*',
                      element: <Navigate to="/" replace />,
                    },
              ],
            },
          ],
          {
            basename: process.env.REACT_APP_BASE_NAME,
          }
        )}
      />
    </Suspense>
  );
};

export default PagesRouter;
